/**
 * Life over the course of a game, and the two different units it is measured in.
 *
 * This lived in the web app, next to the chart that draws it. It is here now
 * because the arithmetic below is the kind that is wrong for a season before
 * anybody notices: a life series is indexed by *turn taken* and almost every
 * question asked of it — "what turn did they go out on", "how much did they
 * take per turn", "how many turns were they in trouble" — is asked in *rounds*.
 * Reading one as the other is silent. It does not throw, it does not look
 * absurd on the screen, it just reports a nine-round game as twenty-five turns
 * and divides by the wrong number. Down here it can be tested against games
 * that really happened.
 *
 * The two units, once, so the rest of the file can name them:
 *
 *   round   what the table calls "turn nine" — one number for everybody, and
 *           the unit `Match.endTurn` is recorded in.
 *   column  one player's turn inside a round. A nine-round three-player game
 *           is twenty-five of them, and that is the resolution the chart draws
 *           at, because "what was I on when it came round to me" cannot be
 *           answered by a single figure covering the whole table.
 */

import { STARTING_LIFE } from './live.js';

/** One life change, however the caller stores them. */
export interface LifeStep {
  /** Whose total moved, as a pilotId. */
  to: string;
  delta: number;
  /** The round. One number for the whole table. */
  turn: number;
  /**
   * Which individual turn inside that round, as an index into the turn list.
   * Absent on games recorded before the counter tracked it.
   */
  step?: number;
}

/** One column of the chart: a single player's turn, and which round it fell in. */
export interface Column {
  /** The round this turn belonged to. */
  turn: number;
  /** Which turn it was inside that round, counting from one. */
  within: number;
  /** "6-2", or "6" for a game that only ever knew rounds. */
  label: string;
}

/**
 * The columns a game gets, one per individual turn.
 *
 * A round is the wrong resolution for the question people ask of the chart:
 * "what was I on when it came round to me" cannot be answered by a single
 * number covering everybody's turn at once. Turn six in a four-player game is
 * four different board states, and drawing it as one column hides every
 * crossover that happened inside it.
 *
 * Falls back to a column per round for games recorded before the counter
 * tracked turns separately — which is the chart exactly as it always was, so
 * nothing already logged is redrawn wrongly.
 */
export function columnsOf(
  turnLog: readonly { seat: number; turn: number }[] | undefined,
  throughTurn: number,
): Column[] {
  if (turnLog && turnLog.length > 0) {
    let within = 0;
    let round = turnLog[0]!.turn;
    return turnLog.map((t) => {
      within = t.turn === round ? within + 1 : 1;
      round = t.turn;
      return { turn: t.turn, within, label: `${t.turn}-${within}` };
    });
  }
  return Array.from({ length: Math.max(1, throughTurn) }, (_, i) => ({
    turn: i + 1,
    within: 1,
    label: `${i + 1}`,
  }));
}

/**
 * Life totals column by column, rebuilt from the changes that produced them.
 *
 * Every line starts at the opening total, so a series is one longer than the
 * column list — index zero is before anybody had done anything.
 *
 * `now` overrides the last column. The live table needs it: a burst of taps is
 * held open for a second or so before it is written down as one change, so the
 * log can be a moment behind the numbers on the table — and a chart whose right
 * edge disagrees with the life total printed above it is worse than no chart.
 */
export function lifeSeries(
  ids: readonly string[],
  changes: readonly LifeStep[],
  columns: readonly Column[],
  now?: Record<string, number>,
): Map<string, number[]> {
  const series = new Map<string, number[]>(ids.map((id) => [id, [STARTING_LIFE]]));

  /*
   * Bucketed once rather than filtered per column. The old version rescanned
   * every change for every player for every turn, which was fine at twelve
   * columns and is not at fifty — and this chart redraws on every poll of a
   * live game.
   *
   * Nested rather than keyed on a joined string. It used to join the two with a
   * NUL, on the reasoning that a pilotId cannot contain one; that is true, and
   * the cost was a source file every `grep` on the repo declined to read as
   * text. A map of maps needs no separator to be safe from.
   */
  const stepped = columns.length > 0 && changes.some((c) => typeof c.step === 'number');
  const buckets = new Map<string, Map<number, number>>();
  for (const change of changes) {
    // A change with no step on a chart drawn by step lands on the first turn of
    // its round, which is the most precision its round alone can support.
    const at = stepped
      ? (change.step ?? columns.findIndex((c) => c.turn === change.turn))
      : change.turn - 1;
    if (at < 0 || at >= columns.length) continue;
    let mine = buckets.get(change.to);
    if (!mine) buckets.set(change.to, (mine = new Map()));
    mine.set(at, (mine.get(at) ?? 0) + change.delta);
  }

  for (let column = 0; column < columns.length; column++) {
    for (const id of ids) {
      const line = series.get(id)!;
      line.push(line[line.length - 1]! + (buckets.get(id)?.get(column) ?? 0));
    }
  }

  if (now) {
    for (const id of ids) {
      const line = series.get(id)!;
      if (now[id] !== undefined) line[line.length - 1] = now[id]!;
    }
  }
  return series;
}

/**
 * What everybody was on at the end of each round.
 *
 * `series` carries a value per column and `line[i + 1]` is the life after
 * column `i`, so the last column of a round is that round's closing figure —
 * which is what falls out of writing each column of a round in turn.
 *
 * This is the whole conversion, in one place: anything that wants to say
 * "turns" in the sense the table means it reads rounds off this rather than
 * indexing the series directly.
 */
export function roundLifeOf(
  ids: readonly string[],
  series: ReadonlyMap<string, readonly number[]>,
  columns: readonly Column[],
): Map<string, Map<number, number>> {
  const byRound = new Map<string, Map<number, number>>();
  for (const id of ids) {
    const line = series.get(id) ?? [];
    const rounds = new Map<number, number>();
    columns.forEach((c, i) => rounds.set(c.turn, line[i + 1] ?? 0));
    byRound.set(id, rounds);
  }
  return byRound;
}

/** How one player's game went, counted in rounds rather than in columns. */
export interface Survival {
  /**
   * The round their life first reached nothing, or null if it never did.
   *
   * Null is not the same as surviving to the end — a player killed by poison or
   * commander damage still has life left — so this says only what the life log
   * says.
   */
  outTurn: number | null;
  /**
   * Rounds they were in the game for, which is what a per-turn average divides
   * by. Falls back to the length of the game for anybody never dropped.
   */
  alive: number;
  /** Rounds they finished as the player closest to dying, ties included. */
  inDanger: number;
  /** Rounds they finished still on the total they sat down with. */
  untouched: number;
  /** Where the line ends. The last column, not a round used as an index. */
  left: number;
}

/**
 * Each player's game in rounds: when they went out, and how it felt getting
 * there.
 *
 * The counting starts at round one, not zero. Index zero of a life series is
 * what everybody sat down with — the moment before the game began — and a loop
 * that starts there hands every player one extra untouched round, and a free
 * round as joint-lowest life, since forty ties with forty.
 */
export function survivalOf(
  ids: readonly string[],
  changes: readonly LifeStep[],
  columns: readonly Column[],
  endTurn: number,
): Map<string, Survival> {
  const series = lifeSeries(ids, changes, columns);
  const roundLife = roundLifeOf(ids, series, columns);

  const out = new Map<string, Survival>();
  for (const id of ids) {
    const line = series.get(id) ?? [];
    // The column they went to nothing on, converted back to the round it sat
    // in. `line[0]` is the life they sat down with, so column `i` is `i + 1`.
    const outIndex = line.findIndex((life) => life <= 0);
    const outTurn = outIndex > 0 ? (columns[outIndex - 1]?.turn ?? null) : null;

    let inDanger = 0;
    let untouched = 0;
    const mineByRound = roundLife.get(id);
    for (let round = 1; round <= endTurn; round++) {
      const mine = mineByRound?.get(round);
      if (mine === undefined) continue;
      if (mine >= STARTING_LIFE) untouched++;
      const lowest = Math.min(
        ...ids.map((other) => roundLife.get(other)?.get(round) ?? Infinity).filter((v) => v > 0),
      );
      if (mine > 0 && mine === lowest) inDanger++;
    }

    out.set(id, {
      outTurn,
      alive: outTurn ?? endTurn,
      inDanger,
      untouched,
      left: line[line.length - 1] ?? 0,
    });
  }
  return out;
}
